import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  Input,
  NumberInput,
  RegularModal,
  UserSelect,
} from '@/components_deprecated'
import { DatePicker } from '@/components'
import { addYears, differenceInWeeks, endOfYear, startOfDay } from 'date-fns'
import type { ShallowUserNode } from '@/modules/users'
import { Field, Form } from '@/modules/users/user-settings/components'
import React, { useEffect, useState } from 'react'
import type {
  EditUserDocumentForm,
  EditUserDocumentPromptResolve,
  UserDocumentForSettings,
} from './types'
import { SelectMultipleUserDocumentCategories } from '@/components_deprecated/Select/SelectMultipleUserDocumentCategories'
import { OwnerNotificationNote } from './owner-notification-note'

interface EditUserDocumentPromptProps {
  userDocument?: UserDocumentForSettings
  /**
   * Whether to ask which user the document belongs to. On for the company-wide
   * documents page, off on a user's own tab, which supplies it itself.
   */
  askForUser?: boolean
  /**
   * Who a new document belongs to, when the caller already knows. An edited
   * document carries its own, and `askForUser` asks for it instead.
   */
  userId?: string
  onSubmit: PromptResolve<EditUserDocumentPromptResolve | null>
}

export const EditUserDocumentPrompt: React.FC<EditUserDocumentPromptProps> = ({
  userDocument,
  askForUser = false,
  userId,
  onSubmit,
}) => {
  const translations = useTranslate({
    createUserDocument: 'settings.create-user-document',
    editUserDocument: 'settings.edit-user-document',
    selectCategories: 'settings.select-categories',

    name: 'common.name',
    user: 'common.user',
    hasNotification: 'settings.has-notification',
    responsibleUser: 'common.responsible-user',
    expirationDate: 'documents.expiration-date',
    notificationTime: 'documents.notification-time',

    continueToFiles: 'documents.continue-to-files',

    validation: {
      mustHaveName: 'settings.validation.user-documents.must-have-name',
      mustSetUser:
        'settings.validation.user-documents.must-set-responsible-user',
      mustSetOwner: 'settings.validation.user-documents.must-set-user',
    },
  })

  const editing = typeof userDocument !== 'undefined'

  const [hasNotification, setHasNotification] = useState(
    !!userDocument?.responsibleUser
  )
  const expirationDate = userDocument?.expirationDate
    ? new Date(userDocument.expirationDate)
    : null
  const notificationTime =
    !userDocument?.expirationDate || !userDocument?.notificationTime
      ? 1
      : differenceInWeeks(
          new Date(userDocument.expirationDate),
          startOfDay(new Date(userDocument.notificationTime))
        )

  const validateResponsibleUser = (val: ShallowUserNode | null) =>
    !hasNotification || val !== null
      ? null
      : translations.validation.mustSetUser

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    validateForm,
    updateForm,
    updateFormErrors,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<EditUserDocumentForm>({
    values: {
      name: userDocument?.name || '',
      categories: userDocument?.categories?.map(category => category.id) || [],
      responsibleUser: userDocument?.responsibleUser ?? null,
      expirationDate: expirationDate,
      notificationTime: notificationTime,
      user: userDocument?.user?.id ?? null,
    },
    validators: {
      name: val => (val ? null : translations.validation.mustHaveName),
      responsibleUser: validateResponsibleUser,
      user: val =>
        !askForUser || val ? null : translations.validation.mustSetOwner,
    },
  })

  useEffect(() => {
    if (hasNotification) {
      updateForm({
        expirationDate: form.expirationDate
          ? form.expirationDate
          : expirationDate ?? new Date(),
        notificationTime: form.notificationTime
          ? form.notificationTime
          : notificationTime ?? 1,
      })
      validateForm(['responsibleUser'])
    } else
      updateFormErrors({
        responsibleUser: null,
        expirationDate: null,
        notificationTime: null,
      })
    // eslint-disable-next-line
  }, [hasNotification])

  return (
    <RegularModal
      title={
        editing
          ? `${translations.editUserDocument}: ${userDocument?.name ?? ''}`
          : translations.createUserDocument
      }
      width="480px"
      submitDisabled={!formValid}
      submitText={translations.continueToFiles}
      onCancel={() => onSubmit(null)}
      onSubmit={submit(form => onSubmit({ form, hasNotification }))}
    >
      <Form>
        {askForUser && !editing && (
          <Field>
            <label>{translations.user}</label>
            <UserSelect
              value={form.user}
              error={errors.user}
              fullWidth
              grayed
              onChange={value => updateForm({ user: value })}
            />
          </Field>
        )}

        <Field>
          <label>{translations.name}</label>
          <Input
            value={form.name}
            error={errors.name}
            fullWidth
            autoFocus
            onChange={handler('name')}
          />
        </Field>

        <Field>
          <label>{translations.selectCategories}</label>
          <SelectMultipleUserDocumentCategories
            selected={form.categories}
            onChange={values => updateForm({ categories: values })}
            placeholder={translations.selectCategories}
            fullWidth
          />
        </Field>

        <Field alignLeft>
          <Checkbox
            checked={hasNotification}
            label={translations.hasNotification}
            boxSize="32px"
            onChange={setHasNotification}
          />
        </Field>

        {hasNotification && (
          <>
            <Field>
              <label>{translations.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser?.id ?? null}
                error={errors.responsibleUser}
                alwaysInclude={form.responsibleUser}
                dropdownPageName="responsible_user"
                fullWidth
                grayed
                onChange={(_, item) =>
                  updateForm({
                    responsibleUser: item?.extra ?? null,
                  })
                }
              />
              <OwnerNotificationNote
                ownerId={userDocument?.user?.id ?? form.user ?? userId ?? null}
                responsibleUserId={form.responsibleUser?.id ?? null}
              />
            </Field>

            <Field>
              <label>{translations.expirationDate}</label>
              <DatePicker
                mode="single"
                typeable
                captionLayout="dropdown"
                // Certificates and licences run for up to a couple of decades.
                endMonth={endOfYear(addYears(new Date(), 30))}
                value={form.expirationDate}
                onChange={expirationDate => updateForm({ expirationDate })}
              />
            </Field>

            <Field>
              <label>{translations.notificationTime}</label>
              <NumberInput
                value={form.notificationTime ?? 1}
                min={0}
                fullWidth
                onChange={handler('notificationTime')}
              />
            </Field>
          </>
        )}
      </Form>
    </RegularModal>
  )
}
